import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dariAlphabet } from './dariAlphabet';
import { dariWords } from './dariWords';

const nameOf = (ch) => {
  const info = dariAlphabet.find((l) => l.dari === ch);
  return info ? info.english : null;
};
const meaningOf = (word) => {
  const w = dariWords.find((x) => x.dari === word);
  return w ? w.english : null;
};

// A small serif "?" medallion that pops a paper card breaking the word down:
// the word, its letters named (in reading order), and the English meaning.
function WordInfoButton({ word }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, above: false });
  const btnRef = useRef(null);

  const place = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const halfW = Math.min(155, vw / 2 - 12);
    let left = r.left + r.width / 2;
    left = Math.max(12 + halfW, Math.min(vw - 12 - halfW, left));
    const above = r.bottom + 230 > vh;
    const top = above ? r.top - 10 : r.bottom + 10;
    setPos({ top, left, above });
  };

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) place();
    setOpen((o) => !o);
  };

  // The popover is fixed-positioned and portalled to <body>, so it tracks the
  // viewport. Close it on scroll (its anchor moves) and keep it placed on resize.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    const onResize = () => place();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const letters = Array.from(word).map((ch) => ({ ch, name: nameOf(ch) }));
  const meaning = meaningOf(word);

  return (
    <>
      <button
        type='button'
        ref={btnRef}
        className={`word-info-btn${open ? ' is-open' : ''}`}
        onClick={toggle}
        aria-label={`Break down ${word}`}
        title='What is this word?'
      >
        ?
      </button>

      {open && createPortal(
        <div className='word-info-backdrop' onClick={() => setOpen(false)}>
          <div
            className={`word-info-pop${pos.above ? ' above' : ''}`}
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
            role='dialog'
            aria-label='Word breakdown'
          >
            <span className='word-info-arrow' />
            <div className='word-info-word' lang='fa' dir='rtl'>{word}</div>
            {meaning && <div className='word-info-meaning'>{meaning}</div>}
            <div className='word-info-rule' />
            <div className='word-info-letters' dir='rtl'>
              {letters.map((l, i) => (
                <div className='wi-letter' key={i} style={{ animationDelay: `${i * 0.04}s` }}>
                  <span className='wi-glyph' lang='fa' dir='rtl'>{l.ch}</span>
                  <span className='wi-name'>{l.name || '·'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default WordInfoButton;
